import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiService from '../../services/apiService';
import { toast } from 'react-toastify';
import JoditEditor from 'jodit-react';

const EditHotel = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [locationName, setLocationName] = useState('');
  const [countrySearch, setCountrySearch] = useState('');
  const [stateSearch, setStateSearch] = useState('');
  const [locationSearch, setLocationSearch] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [showAmenitiesDropdown, setShowAmenitiesDropdown] = useState(false);
  const countryDropdownRef = useRef(null);
  const stateDropdownRef = useRef(null);
  const locationDropdownRef = useRef(null);
  const typeDropdownRef = useRef(null);
  const amenitiesDropdownRef = useRef(null);
  
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    fullAddress: '',
    countryId: '',
    stateId: '',
    cityId: '',
    currency: '',
    price: '',
    discountPrice: '',
    property: '',
    checkIn: '',
    checkOut: '',
    type: [],
    amenities: [],
    about: '',
    importantNote: '',
    image: null
  });

  const [imagePreview, setImagePreview] = useState('');
  const [gallery, setGallery] = useState([]);
  const [newGalleryFiles, setNewGalleryFiles] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [locations, setLocations] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [hotelTypes, setHotelTypes] = useState([]);
  const [amenities, setAmenities] = useState([]);
  
  const fileInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const mediaUrl = process.env.REACT_APP_MEDIA_URL;

  const editorConfig = {
    readonly: false,
    height: 300,
    toolbar: true,
    uploader: { insertImageAsBase64URI: false },
    removeButtons: ['image', 'file']
  };

  useEffect(() => {
    fetchHotelData();
  }, [id]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target)) setShowCountryDropdown(false);
      if (stateDropdownRef.current && !stateDropdownRef.current.contains(event.target)) setShowStateDropdown(false);
      if (locationDropdownRef.current && !locationDropdownRef.current.contains(event.target)) setShowLocationDropdown(false);
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(event.target)) setShowTypeDropdown(false);
      if (amenitiesDropdownRef.current && !amenitiesDropdownRef.current.contains(event.target)) setShowAmenitiesDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (formData.countryId) {
      fetchStateLocations('country');
    }
  }, [formData.countryId]);

  useEffect(() => {
    if (formData.stateId) {
      fetchStateLocations('state', formData.cityId);
    }
  }, [formData.stateId]);

  const fetchHotelData = async () => {
    try {
      setLoading(true);
      const response = await apiService.get(`editHotel/${id}`);
      const hotelData = response.data.data;
      
      setFormData({
        id: hotelData._id,
        name: hotelData.name || '',
        fullAddress: hotelData.fullAddress || '',
        countryId: hotelData.countryId || '',
        stateId: hotelData.stateId || '',
        cityId: hotelData.cityId || '',
        currency: hotelData.currency || '',
        price: hotelData.price || '',
        discountPrice: hotelData.discountPrice || '',
        property: hotelData.property || '',
        checkIn: hotelData.checkIn || '',
        checkOut: hotelData.checkOut || '',
        type: hotelData.type || [],
        amenities: hotelData.amenities || [],
        about: hotelData.about || '',
        importantNote: hotelData.importantNote || '',
        image: null
      });
      
      setImagePreview(hotelData.image ? `${mediaUrl}${hotelData.image}` : '');
      setGallery(hotelData.gallery || []);
      setCountries(response.data.country || []);
      setStates(response.data.states || []);
      setCurrencies(response.data.currency || []);
      setHotelTypes(response.data.hotelType || []);
      setAmenities(response.data.amenities || []);
      
      const selectedCountry = response.data.country?.find(c => c._id === hotelData.countryId);
      if (selectedCountry) setCountrySearch(selectedCountry.name);
      
      const selectedState = response.data.states?.find(s => s._id === hotelData.stateId);
      if (selectedState) setStateSearch(selectedState.name);
      
      const selectedLocation = response.data.locations?.find(l => l._id === hotelData.cityId);
      if (selectedLocation) setLocationSearch(selectedLocation.name);
    } catch (error) {
      toast.error('Failed to fetch hotel data');
    } finally {
      setLoading(false);
    }
  };

  const fetchStateLocations = async (type, selectedCityId = null) => {
    try {
      const response = await apiService.post('stateLocationList', {
        countryId: formData.countryId,
        stateId: formData.stateId,
        type
      });
      
      if (response.message === 'success') {
        if (type === 'country') {
          setStates(response.data.states || []);
        }
        setLocations(response.data.locations || []);
        if (selectedCityId) {
          setFormData(prev => ({ ...prev, cityId: selectedCityId }));
        }
      }
    } catch (error) {
      console.error('Failed to fetch locations');
    }
  };

  const toggleSelection = (field, value) => {
    const current = formData[field];
    const updated = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
    setFormData({ ...formData, [field]: updated });
  };

  const getSelectedText = (field, items) => {
    const selected = items.filter(item => formData[field].includes(item._id));
    return selected.length > 0 ? `${selected.length} selected` : `Select ${field}`;
  };

  const filteredCountries = countries.filter(c => c.name.toLowerCase().includes(countrySearch.toLowerCase()));
  const filteredStates = states.filter(s => s.name.toLowerCase().includes(stateSearch.toLowerCase()));
  const filteredLocations = locations.filter(l => l.name.toLowerCase().includes(locationSearch.toLowerCase()));

  const handleCountryChange = async (country) => {
    setFormData({ ...formData, countryId: country._id, stateId: '', cityId: '' });
    setCountrySearch(country.name);
    setStateSearch('');
    setLocationSearch('');
    setShowCountryDropdown(false);
    try {
      const response = await apiService.post('stateLocationList', { countryId: country._id, stateId: '', type: 'country' });
      if (response.message === 'success') {
        setStates(response.data.states || []);
        setLocations(response.data.locations || []);
      }
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  };

  const handleStateChange = async (state) => {
    setFormData({ ...formData, stateId: state._id, cityId: '' });
    setStateSearch(state.name);
    setLocationSearch('');
    setShowStateDropdown(false);
    try {
      const response = await apiService.post('stateLocationList', { countryId: formData.countryId, stateId: state._id, type: 'state' });
      if (response.message === 'success') {
        setLocations(response.data.locations || []);
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const handleStateSearchChange = (value) => {
    setStateSearch(value);
    setShowStateDropdown(true);
    // If state search is cleared, clear state and locations
    if (value === '') {
      setFormData({ ...formData, stateId: '', cityId: '' });
      setLocations([]);
      setLocationSearch('');
    }
  };

  const handleLocationChange = (location) => {
    setFormData({ ...formData, cityId: location._id });
    setLocationSearch(location.name);
    setShowLocationDropdown(false);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('File size must be 2MB or less');
        e.target.value = '';
        return;
      }
      setFormData({ ...formData, image: file });
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleGalleryChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = [];
    
    for (const file of files) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error(`File "${file.name}" is too large (max 2MB)`);
        continue;
      }
      validFiles.push(file);
    }
    
    setNewGalleryFiles(prev => [...prev, ...validFiles]);
  };

  const removeGalleryImage = async (imageName, index) => {
    try {
      await apiService.post('deleteImage', {
        type: 'hotel',
        typeId: id,
        imageName
      });
      setGallery(prev => prev.filter((_, i) => i !== index));
      toast.success('Image deleted successfully');
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  const removeNewGalleryImage = (index) => {
    setNewGalleryFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddLocation = async (e) => {
    e.preventDefault();
    if (!formData.countryId || !formData.stateId) {
      toast.error('Select country and state first');
      return;
    }
    
    try {
      const response = await apiService.post('addVendorLocation', {
        name: locationName,
        countryId: formData.countryId,
        stateId: formData.stateId
      });
      
      if (response.status) {
        toast.success(response.message);
        setLocations(prev => [...prev, response.data]);
        setFormData(prev => ({ ...prev, cityId: response.data._id }));
        setShowLocationModal(false);
        setLocationName('');
      }
    } catch (error) {
      toast.error('Failed to add location');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const totalImages = gallery.length + newGalleryFiles.length;
    if (totalImages > 10) {
      toast.error('You can upload maximum 10 images!!');
      return;
    }
    if (totalImages < 4) {
      toast.error('Please upload atleast 4 images of your hotel!!');
      return;
    }
    
    setSubmitting(true);
    try {
      const data = new FormData();
      Object.keys(formData).forEach(key => {
        if (key === 'type' || key === 'amenities') {
          formData[key].forEach(val => data.append(`${key}[]`, val));
        } else if (key !== 'image' && formData[key]) {
          data.append(key, formData[key]);
        }
      });
      
      if (formData.image) data.append('image', formData.image);
      newGalleryFiles.forEach(file => data.append('images', file));
      
      await apiService.postFormData('updateHotel', data);
      toast.success('Hotel updated successfully');
      navigate('/hotel');
    } catch (error) {
      toast.error('Something went wrong, try again!!');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#009ef7]"></div>
      </div>
    );
  }

  return (
    <div className="flex-1 p-0">
      <div className="mb-3">
        <nav className="flex items-center space-x-2 text-sm text-gray-600">
          <a href="/dashboard" className="hover:text-gray-900">Home</a>
          <span className="text-gray-400">/</span>
          <a href="/hotel" className="hover:text-gray-900">Hotels</a>
          <span className="text-gray-400">/</span>
          <span className="text-gray-900">Edit Hotel</span>
        </nav>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-5">
            <div className="relative inline-block">
              <div
                className="w-72 h-60 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center overflow-hidden"
                style={{
                  backgroundImage: imagePreview ? `url(${imagePreview})` : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              >
                {!imagePreview && <i className="bi bi-image text-4xl text-gray-400"></i>}
              </div>
              <label
                className="absolute bottom-2 right-2 w-6 h-6 bg-white rounded-full shadow flex items-center justify-center cursor-pointer hover:bg-gray-50"
                title="Change image"
              >
                <i className="bi bi-pencil text-xs"></i>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,.webp"
                  onChange={handleImageChange}
                  className="hidden"
                />
              </label>
            </div>
            <p className="text-sm text-gray-500 mt-2">Allowed file types: png, jpg, jpeg</p>
          </div>

          <div className="col-span-7 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hotel Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.fullAddress}
                onChange={(e) => setFormData({ ...formData, fullAddress: e.target.value })}
                placeholder="Enter hotel full address"
                className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
                required
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Country <span className="text-red-500">*</span>
                </label>
                <div className="relative" ref={countryDropdownRef}>
                  <input
                    type="text"
                    value={countrySearch}
                    onChange={(e) => { setCountrySearch(e.target.value); setShowCountryDropdown(true); }}
                    onFocus={() => setShowCountryDropdown(true)}
                    placeholder="Search Country"
                    className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
                    required={!formData.countryId}
                  />
                  {showCountryDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {filteredCountries.length > 0 ? filteredCountries.map(c => (
                        <div key={c._id} onClick={() => handleCountryChange(c)} className="px-2 sm:px-4 py-2 hover:bg-gray-100 cursor-pointer">{c.name}</div>
                      )) : <div className="px-2 sm:px-4 py-2 text-gray-500">No countries found</div>}
                    </div>
                  )}
                </div>
                <input type="hidden" name="countryId" value={formData.countryId} required />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  State <span className="text-red-500">*</span>
                </label>
                <div className="relative" ref={stateDropdownRef}>
                  <input
                    type="text"
                    value={stateSearch}
                    onChange={(e) => handleStateSearchChange(e.target.value)}
                    onFocus={() => setShowStateDropdown(true)}
                    placeholder="Search State"
                    className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
                    required={!formData.stateId}
                  />
                  {showStateDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {filteredStates.length > 0 ? filteredStates.map(s => (
                        <div key={s._id} onClick={() => handleStateChange(s)} className="px-2 sm:px-4 py-2 hover:bg-gray-100 cursor-pointer">{s.name}</div>
                      )) : <div className="px-2 sm:px-4 py-2 text-gray-500">No states found</div>}
                    </div>
                  )}
                </div>
                <input type="hidden" name="stateId" value={formData.stateId} required />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                <div className="relative" ref={locationDropdownRef}>
                  <input
                    type="text"
                    value={locationSearch}
                    onChange={(e) => { setLocationSearch(e.target.value); setShowLocationDropdown(true); }}
                    onFocus={() => setShowLocationDropdown(true)}
                    placeholder="Search Location"
                    className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
                  />
                  {showLocationDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {filteredLocations.length > 0 ? filteredLocations.map(l => (
                        <div key={l._id} onClick={() => handleLocationChange(l)} className="px-2 sm:px-4 py-2 hover:bg-gray-100 cursor-pointer">{l.name}</div>
                      )) : <div className="px-2 sm:px-4 py-2 text-gray-500">No locations found</div>}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setShowLocationModal(true)}
              className="px-2 sm:px-4 py-2 text-sm font-medium text-white rounded-lg"
              style={{ backgroundColor: '#009ef7' }}
            >
              <i className="bi bi-plus mr-1"></i>Add New Location
            </button>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-4 mt-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Currency <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.currency}
              onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
              className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
              required
            >
              <option value="">Select</option>
              {currencies.map(c => (
                <option key={c._id} value={c._id}>{c.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Price/Room <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: e.target.value })}
              className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Discounted Price/Room</label>
            <input
              type="number"
              value={formData.discountPrice}
              onChange={(e) => setFormData({ ...formData, discountPrice: e.target.value })}
              className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Property Class <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.property}
              onChange={(e) => setFormData({ ...formData, property: e.target.value })}
              className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
              required
            >
              <option value="">Select</option>
              {[1, 2, 3, 4, 5].map(n => (
                <option key={n} value={n}>{n} Star</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-4 mt-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Check-In Time</label>
            <input
              type="time"
              value={formData.checkIn}
              onChange={(e) => setFormData({ ...formData, checkIn: e.target.value })}
              className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Check-Out Time</label>
            <input
              type="time"
              value={formData.checkOut}
              onChange={(e) => setFormData({ ...formData, checkOut: e.target.value })}
              className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Hotel Type <span className="text-red-500">*</span>
            </label>
            <div className="relative" ref={typeDropdownRef}>
              <div 
                onClick={() => setShowTypeDropdown(!showTypeDropdown)} 
                className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg cursor-pointer bg-white"
              >
                {getSelectedText('type', hotelTypes)}
              </div>
              {showTypeDropdown && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {hotelTypes.map(t => (
                    <div 
                      key={t._id} 
                      onClick={() => toggleSelection('type', t._id)} 
                      className="px-2 sm:px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center"
                    >
                      <input 
                        type="checkbox" 
                        checked={formData.type.includes(t._id)} 
                        onChange={() => {}} 
                        className="mr-2" 
                      />
                      {t.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Amenities <span className="text-red-500">*</span>
            </label>
            <div className="relative" ref={amenitiesDropdownRef}>
              <div 
                onClick={() => setShowAmenitiesDropdown(!showAmenitiesDropdown)} 
                className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg cursor-pointer bg-white"
              >
                {getSelectedText('amenities', amenities)}
              </div>
              {showAmenitiesDropdown && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {amenities.map(a => (
                    <div 
                      key={a._id} 
                      onClick={() => toggleSelection('amenities', a._id)} 
                      className="px-2 sm:px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center"
                    >
                      <input 
                        type="checkbox" 
                        checked={formData.amenities.includes(a._id)} 
                        onChange={() => {}} 
                        className="mr-2" 
                      />
                      {a.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            About Hotel <span className="text-red-500">*</span>
          </label>
          <JoditEditor
            value={formData.about}
            config={editorConfig}
            onBlur={newContent => setFormData({ ...formData, about: newContent })}
          />
        </div>

        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">Important Note</label>
          <JoditEditor
            value={formData.importantNote}
            config={editorConfig}
            onBlur={newContent => setFormData({ ...formData, importantNote: newContent })}
          />
        </div>

        <div className="mt-6">
          <h3 className="text-lg font-semibold mb-4">Gallery</h3>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Upload hotel gallery images (Maximum: 10) <span className="text-red-500">*</span>
          </label>
          <input
            ref={galleryInputRef}
            type="file"
            multiple
            accept=".png,.jpg,.jpeg,.webp"
            onChange={handleGalleryChange}
            className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg"
          />
          
          <div className="grid grid-cols-5 gap-4 mt-4">
            {gallery.map((img, index) => (
              <div key={index} className="relative">
                <div
                  className="w-full h-36 rounded-lg border-2 border-gray-300"
                  style={{
                    backgroundImage: `url(${mediaUrl}${img})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                />
                <button
                  type="button"
                  onClick={() => removeGalleryImage(img, index)}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-600 text-white rounded-full flex items-center justify-center hover:bg-red-700"
                >
                  ×
                </button>
              </div>
            ))}
            {newGalleryFiles.map((file, index) => (
              <div key={`new-${index}`} className="relative">
                <div
                  className="w-full h-36 rounded-lg border-2 border-gray-300"
                  style={{
                    backgroundImage: `url(${URL.createObjectURL(file)})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                />
                <button
                  type="button"
                  onClick={() => removeNewGalleryImage(index)}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-600 text-white rounded-full flex items-center justify-center hover:bg-red-700"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={() => navigate('/hotel')}
            className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-6 py-2 text-sm font-medium text-white rounded-lg"
            style={{ backgroundColor: '#009ef7' }}
          >
            {submitting ? 'Please wait...' : 'Submit'}
          </button>
        </div>
      </form>

      {showLocationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Add Location</h3>
              <button onClick={() => setShowLocationModal(false)} className="text-gray-400 hover:text-gray-600">
                <i className="bi bi-x text-2xl"></i>
              </button>
            </div>
            <form onSubmit={handleAddLocation} className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={locationName}
                  onChange={(e) => setLocationName(e.target.value)}
                  placeholder="Enter Location Name"
                  className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
                  required
                />
              </div>
              <div className="flex flex-col sm:flex-row justify-center gap-3">
                <button
                  type="button"
                  onClick={() => setShowLocationModal(false)}
                  className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 text-sm font-medium text-white rounded-lg"
                  style={{ backgroundColor: '#009ef7' }}
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditHotel;
